import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu as MenuIcon, Play, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
const columns = [{
  id: 'corporate',
  title: 'Corporate',
  video: 'https://cdn.magicpatterns.com/uploads/rtaX1BcQNKEW21Dp7G6hLa/Women%E2%80%99s%20Caucus%20Brunch%20Set-up.mp4',
  poster: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&q=80&w=1200'
}, {
  id: 'weddings',
  title: 'Weddings',
  video: 'https://cdn.magicpatterns.com/uploads/t56AP9tMtyGmTaR1jdHNXe/Red%20Roses%20Birthday%20Lux%20Event.mp4',
  poster: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=1200'
}, {
  id: 'meal-prep',
  title: 'Meal Prep',
  video: 'https://cdn.magicpatterns.com/uploads/tWeER3SAVhy5L91uejKwKb/Graduation%20Catering%20Party.mp4',
  poster: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?auto=format&fit=crop&q=80&w=1200'
}];
interface HeroProps {
  onOpenQuote: () => void;
}
export function Hero({
  onOpenQuote
}: HeroProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Menu',
    path: '/menu'
  }, {
    name: 'Events',
    path: '/events'
  }, {
    name: 'Story',
    path: '/story'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <section className="relative h-[85vh] md:h-[90vh] w-full overflow-hidden flex flex-col md:flex-row bg-brand-black" data-id="element-174">
      {/* Top Navigation Bar */}
      <div className="absolute top-0 left-0 w-full z-50 flex flex-col px-4 md:px-8 py-4 md:py-6" data-id="element-175">
        <div className="flex items-center justify-between bg-brand-black/40 backdrop-blur-sm px-4 md:px-8 py-3 md:py-4 rounded-sm border border-white/10 w-full" data-id="element-176">
          <div className="flex items-center" data-id="element-177">
            <img src="https://cdn.magicpatterns.com/uploads/otjuHu8XNsL2f4dQ3LfjEG/ChatGPT_Image_Apr_22,_2026,_03_42_32_PM.png" alt="Versatile LLC" className="h-12 md:h-20 w-auto object-contain mr-4 md:mr-12" data-id="element-178" />
            <nav className="hidden xl:flex items-center gap-10" data-id="element-179">
              {navLinks.map(link => {
              const isActive = location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path);
              return <Link key={link.name} to={link.path} className={`text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${isActive ? 'text-brand-gold' : 'text-white hover:text-brand-gold'}`} data-id="element-180">
                    {link.name}
                  </Link>;
            })}
            </nav>
          </div>

          <div className="flex items-center gap-4 md:gap-8" data-id="element-181">
            <button onClick={onOpenQuote} className="hidden xl:block bg-brand-gold text-white px-10 py-6 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-brand-black transition-all shadow-2xl leading-tight text-center" data-id="element-182">
              Request a Custom <br data-id="element-183" /> Quote
            </button>
            <button className="xl:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-184">
              {isMobileMenuOpen ? <X size={24} data-id="element-185" /> : <MenuIcon size={24} data-id="element-186" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-187">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="xl:hidden mt-2 bg-brand-black/95 backdrop-blur-md border border-white/10 rounded-sm p-4 flex flex-col gap-4 w-full" data-id="element-188">
              {navLinks.map(link => {
            const isActive = location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path);
            return <Link key={link.name} to={link.path} onClick={() => setIsMobileMenuOpen(false)} className={`text-sm font-bold uppercase tracking-[0.2em] transition-colors py-2 border-b border-white/10 ${isActive ? 'text-brand-gold' : 'text-white hover:text-brand-gold'}`} data-id="element-189">
                    {link.name}
                  </Link>;
          })}
              <button onClick={() => {
            onOpenQuote();
            setIsMobileMenuOpen(false);
          }} className="bg-brand-gold text-white px-4 py-4 rounded-sm text-xs font-bold uppercase tracking-widest w-full mt-2" data-id="element-190">
                Request a Quote
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* 3-Column Background Grid with Video */}
      <div className="absolute inset-0 flex flex-col md:flex-row z-0" data-id="element-191">
        {columns.map(col => <motion.div key={col.id} className="relative flex-1 h-full overflow-hidden border-r border-white/5 last:border-r-0 group" whileHover={{
        flex: 1.5
      }} transition={{
        duration: 0.6,
        ease: 'circOut'
      }} data-id="element-192">
            <div className="absolute inset-0" data-id="element-193">
              <video autoPlay muted loop playsInline poster={col.poster} className="w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 group-hover:opacity-70 transition-all duration-700" data-id="element-194">
                <source src={col.video} type="video/mp4" data-id="element-195" />
              </video>
              <div className="absolute inset-0 bg-brand-black/20 group-hover:bg-transparent transition-colors duration-700" data-id="element-196" />
            </div>

            <div className="absolute bottom-12 left-0 w-full text-center pointer-events-none z-10" data-id="element-197">
              <span className="text-sm md:text-base font-bold uppercase tracking-[0.3em] text-white/80 group-hover:text-brand-gold transition-colors drop-shadow-md" data-id="element-198">
                {col.title}
              </span>
            </div>
          </motion.div>)}
      </div>

      {/* Centered Content Overlay */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 md:px-6 pointer-events-none" data-id="element-199">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-200">
          <h1 className="text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif leading-tight mb-6 md:mb-8" data-id="element-201">
            DMV Catering <br data-id="element-202" />
            <span className="text-brand-gold italic" data-id="element-203">for Any Event.</span>
          </h1>

          <motion.button onClick={onOpenQuote} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="pointer-events-auto bg-brand-gold text-white px-8 py-4 md:px-12 md:py-6 rounded-sm text-xs md:text-sm font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] hover:bg-white hover:text-brand-black transition-all duration-300 shadow-2xl" data-id="element-204">
            Book Your Event
          </motion.button>
        </motion.div>
      </div>

      {/* Bottom Gradient for Transition */}
      <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white/80 to-transparent z-20 pointer-events-none" data-id="element-205" />
    </section>;
}